import React, { useCallback, useMemo, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Button, IconButton, Stack, Typography } from "@mui/material";

import { Characteristic, CharacteristicData, useForm } from "..";
import { currenciesArray } from "../../../../constants";
import { DataItem } from "./DataItem";

interface Props {
  characteristic: Characteristic;
  onClose: () => void;
}

const decimalPercentage = 2;
export const currencyCharVarName = "Currency";

export const MultiDataEditPopup: React.FC<Props> = ({ characteristic, onClose }) => {
  const { id, name } = characteristic;

  const isCurrency = name === currencyCharVarName;

  const { getManyData, buildNewCharData, handleManyCharData, viewBy } = useForm();

  const data = getManyData({
    characteristic_id: id,
    investor_id: viewBy?.investor?.id || null,
    round_id: viewBy?.round?.id || null
  });

  const [localState, setLocalState] = useState<CharacteristicData[]>(data);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const totalPercentage: number = useMemo(() => {
    return localState.reduce((acc, item) => (item.is_deleted ? acc : (acc += item.percentage)), 0);
  }, [localState]);

  const addItem = () => {
    const totalPercentage = localState.reduce((acc, item) => (item.is_deleted ? acc : (acc += item.percentage)), 0);

    const delta = 100 - totalPercentage;

    const newCharData = buildNewCharData({
      characteristic_id: id,
      percentage: delta,
      investor_id: viewBy?.investor?.id || null,
      round_id: viewBy?.round?.id || null
    });

    // check if we have local item with the same id or higher
    const startWith = Number(newCharData.id);

    // Find max id of the characteristics data
    const maxId = localState.reduce((acc, item) => {
      const numberId = Number(item.id);
      return numberId > acc ? numberId : acc;
    }, startWith);

    // Decide what is higher add 1 and stringify it.
    const finalId = String((startWith > maxId ? startWith : maxId) + 1);

    setLocalState((prevState) => [...prevState, { ...newCharData, id: finalId, characteristic_id: id }]);

    // Delay scroll to the bottom of the container
    setTimeout(() => {
      containerRef.current?.scrollTo({
        top: containerRef.current?.scrollHeight + 10 + (localState.length + 1) * 16,
        behavior: "smooth"
      });
    }, 100);
  };

  const removeItem = (id: CharacteristicData["id"]) => {
    setLocalState((prevState) => {
      const updatedState =
        typeof id === "number"
          ? prevState.map((item) => (item.id === id ? { ...item, is_deleted: true, percentage: 0 } : item))
          : prevState.filter((item) => item.id !== id);

      // For currency characteristics, ensure there's always a default if the removed item was default
      if (isCurrency) {
        const removedItem = prevState.find((item) => item.id === id);
        const wasDefault = removedItem?.is_default;

        if (wasDefault) {
          const availableItems = updatedState.filter((item) => !item.is_deleted);

          // If we have available items, set the first one as default
          if (availableItems.length > 0) {
            const firstAvailableId = availableItems[0].id;
            return updatedState.map((item) => ({
              ...item,
              is_default: item.id === firstAvailableId
            }));
          }
        }
      }
      return updatedState;
    });
  };

  const updateItem = ({
    id,
    ...rest
  }: Pick<CharacteristicData, "id"> & Partial<Pick<CharacteristicData, "value" | "percentage">>) => {
    setLocalState((prevState) => prevState.map((item) => (item.id === id ? { ...item, ...rest } : item)));
  };

  const handleSplitPercentage = useCallback(() => {
    if (!localState.length) return;
    // state includes also deleted items, so we need to separate them and calculate percentage for available items only
    const { available, deleted } = localState.reduce(
      (acc, item) => {
        if (item.is_deleted) {
          acc.deleted.push(item);
        } else {
          acc.available.push(item);
        }
        return acc;
      },
      { available: [], deleted: [] } as { available: CharacteristicData[]; deleted: CharacteristicData[] }
    );

    const splitHundred = available.map((item) => ({
      ...item,
      percentage: Number((100 / available.length).toFixed(decimalPercentage))
    }));

    const delta = 100 - splitHundred.reduce((acc, item) => (acc += Number(item.percentage)), 0);
    if (!delta) {
      return setLocalState([...splitHundred, ...deleted]);
    }

    const addDeltaToLast = splitHundred.map((item, index) =>
      index ? item : { ...item, percentage: item.percentage + delta }
    );

    const sum = addDeltaToLast.reduce((acc, item) => (acc += Number(item.percentage)), 0);

    if (sum !== 100) {
      return console.log("Sub is not equal 100", sum);
    }
    setLocalState([...addDeltaToLast, ...deleted]);
  }, [localState, setLocalState]);

  // This currencies should be excluded from the select options to avoid currency duplication
  const filterCurrenciesOptions = (selectedCurr: string | null): string[] => {
    const selectedCurrencies: string[] = [];

    // iterate the data items, put all currencies except selected one
    // value is because of only specific characteristic as "Currency"
    console.log(localState);
    localState.forEach(({ value }) => {
      if (!value) return;
      if (selectedCurr && value === selectedCurr) return;
      selectedCurrencies.push(value);
    });

    return currenciesArray.filter((cur) => !selectedCurrencies.includes(cur));
  };

  const handleSubmit = () => {
    handleManyCharData(localState);
    onClose();
  };

  const isDisabled = useMemo(() => {
    // Button is disabled when:
    // 1. Total percentage is not 0 or 100
    // 2. There are non-deleted items with empty values
    // 3. If there are any non-deleted items, total percentage must be 100%
    const hasInvalidPercentage = totalPercentage !== 0 && totalPercentage !== 100;
    const hasEmptyValue = localState.some((item) => !item.is_deleted && !item.value);
    const allItemsDeleted = localState.every((item) => item.is_deleted);
    const hasItemsButInvalidTotal = !allItemsDeleted && totalPercentage !== 100;

    return hasInvalidPercentage || (!allItemsDeleted && hasEmptyValue) || hasItemsButInvalidTotal;
  }, [localState, totalPercentage]);

  return (
    <Stack
      sx={{
        width: 520,
        maxHeight: 340,
        overflow: "hidden",
        borderRadius: "8px",
        p: "24px",
        gap: "20px",
        boxShadow: "sm"
      }}
    >
      <Typography sx={{ fontSize: "18px", lineHeight: "24px", color: "#071F35", fontWeight: 400 }}>{name}</Typography>
      <Stack sx={{ gap: "16px", overflow: "hidden" }}>
        <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
          <Typography
            sx={{
              fontSize: "16px",
              color: "#496682",
              lineHeight: "24px",
              fontWeight: 400
            }}
          >
            Total percentages should add up to 100%
          </Typography>
          <Button size="sm" sx={{ bgcolor: "#f5f6f7" }} onClick={handleSplitPercentage}>
            Spread equally
          </Button>
        </Stack>
        <Stack sx={{ gap: "16px", overflow: "auto", py: "5px" }} ref={containerRef}>
          {localState
            .filter((item) => !item.is_deleted)
            .map((item, _, array) => (
              <DataItem
                key={item.id}
                characteristicData={item}
                // onRemove={() => removeItem(item.id)}
                {...(!(isCurrency && array.length <= 1) && { onRemove: () => removeItem(item.id) })}
                onUpdate={(params) => updateItem({ id: item.id, ...params })}
                characteristic={characteristic}
                filterCurrenciesOptions={() => filterCurrenciesOptions(item.value)} // "Currency" characteristic has "value" field as main field
              />
            ))}
        </Stack>
        <IconButton onClick={addItem} sx={{ bgcolor: "#f5f6f7" }}>
          <CloseIcon sx={{ transform: "rotate(45deg)" }} />
        </IconButton>
      </Stack>
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "15px", lineHeight: "20px", color: "#496682" }}>
          Total: {totalPercentage}%
        </Typography>
        <Stack sx={{ flexDirection: "row", gap: "8px" }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={isDisabled} variant="contained" onClick={handleSubmit}>
            Apply
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
